import _ from 'underscore';
import classNames from 'classnames';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Glyphicon } from '@bingads-webui-react/primitive';

import { StepsContext } from './index';
import { getStepStatusAriaLabel } from '../util/get-step-status-aria-label';

/**
 * Recursively iterate through the steps and create lists and list items.
 * @param {Array} steps - Step tree.
 * @param {Function} [getItemClassName] - Function that accepts a step and returns a className.
 * @param {Function} [getItemGlyphicon] - Function that accepts a step and returns a glyphicon
 * identifier, e.g. 'ba-CompletedSolid'.
 * @param {Object} ariaI18n - i18n library used for aria-label, if missing will fallback to English.
 * @returns {ReactNode[]} - Array of list items.
 */
function getListItems(steps, getItemClassName, getItemGlyphicon, ariaI18n) {
  let foundSelectedStep = false;
  return _.map(steps, (step) => {
    if (!step.title) {
      return null;
    }

    const stepData = {
      ...step,
      isBehind: !step.containsSelected && !step.isSelected && !foundSelectedStep,
      isAhead: !step.containsSelected && !step.isSelected && foundSelectedStep,
    };

    const listItemClassNames = [{
      'step-progress-list-item': true,
      'step-progress-list-item-selected': stepData.isSelected,
      'step-progress-list-item-contains-selected': stepData.containsSelected,
      'step-progress-list-item-behind': stepData.isBehind,
      'step-progress-list-item-ahead': stepData.isAhead,
    }];

    if (stepData.isSelected || stepData.containsSelected) {
      foundSelectedStep = true;
    }

    const customClassNames = _.isFunction(getItemClassName) ? getItemClassName(stepData) : null;
    const glyph = _.isFunction(getItemGlyphicon) ? getItemGlyphicon(stepData) : null;

    const isCustomGlyphRenderer = _.isFunction(step.renderGlyph);
    const isCustomTitleRenderer = _.isFunction(step.renderTitle);
    const isCustomDescRenderer = _.isFunction(step.renderDescription);
    const stepStatusAriaLabel = getStepStatusAriaLabel(stepData, ariaI18n);

    return (
      <li
        key={step.path}
        data-step-path={step.path}
        className={classNames(listItemClassNames, customClassNames)}
        aria-label={stepStatusAriaLabel}
      >
        {
          (isCustomGlyphRenderer && step.renderGlyph(step)) ||
          (glyph && <Glyphicon
            className="step-progress-list-item-glyphicon"
            glyph={glyph}
          />)
        }

        {(isCustomTitleRenderer && step.renderTitle(step)) ||
          (<span className="step-progress-list-item-title">{step.title}</span>)
        }

        {(isCustomDescRenderer && step.renderDescription(step)) ||
          (step.description && <span className="step-progress-list-item-description">{step.description}</span>)
        }

        {step.containsSelected &&
          <ol className="step-progress-list-item-list">
            {getListItems(step.steps, getItemClassName, getItemGlyphicon, ariaI18n)}
          </ol>
        }
      </li>
    );
  });
}

export function StepProgressList(props) {
  const { steps } = useContext(StepsContext);
  const {
    id,
    className,
    getItemClassName,
    getItemGlyphicon,
    ariaI18n,
  } = props;

  if (steps) {
    return (
      <div id={id} className={className}>
        <ol className="step-progress-list">
          {getListItems(steps, getItemClassName, getItemGlyphicon, ariaI18n)}
        </ol>
      </div>
    );
  }

  return null;
}

StepProgressList.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  /**
   * Function that takes in a step and returns a custom className.
   */
  getItemClassName: PropTypes.func,
  /**
   * Function that takes in a step and returns a custom glyphicon identifier.
   */
  getItemGlyphicon: PropTypes.func,
  ariaI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }),
};

StepProgressList.defaultProps = {
  id: null,
  className: null,
  getItemClassName: null,
  getItemGlyphicon: null,
  ariaI18n: null,
};
